import { clamp } from 'lodash';
import type { Point } from '@ardoq/math';
import { Rect } from '../../Rect';
import { Size } from '../../Size';
import type { ChartWindowState, NumericRange } from './types';

export const MAX_RADIUS_FACTOR = 0.1;
const ZOOM_INCREMENT = 0.1;
const ZOOM_DELTA_DIVISOR = 100;
export const SCALE_MINIMUM = MAX_RADIUS_FACTOR * 2; // min scale, or max zoom, is roughly the point where maxRadius * 2 === minDimension

export const rect = ([centerX, centerY]: Point, scale: number): Rect =>
  new Rect(centerX - 0.5 * scale, centerY - 0.5 * scale, scale, scale);

const zoomRect = (
  { left, right, top, bottom }: Rect,
  [offsetPortionX, offsetPortionY]: Point,
  zoomFactor: number
): Rect => {
  const cx = left + offsetPortionX;
  const cy = top + offsetPortionY;
  const zoomLeft = clamp(cx - zoomFactor * (cx - left), 0, 1);
  const zoomRight = clamp(cx + zoomFactor * (right - cx), 0, 1);
  const zoomTop = clamp(cy - zoomFactor * (cy - top), 0, 1);
  const zoomBottom = clamp(cy + zoomFactor * (bottom - cy), 0, 1);
  return new Rect(
    zoomLeft,
    zoomTop,
    zoomRight - zoomLeft,
    zoomBottom - zoomTop
  );
};

export const zoom = (
  area: Size,
  windowCenter: Point,
  [offsetX, offsetY]: Point,
  windowScale: number,
  delta: number
): ChartWindowState => {
  const zoomFactor =
    1 + clamp(delta / ZOOM_DELTA_DIVISOR, -1, 1) * ZOOM_INCREMENT;
  const oldScale = windowScale;
  const oldRect = rect(windowCenter, windowScale);

  const zoomed = zoomRect(
    oldRect,
    [(oldScale * offsetX) / area.width, (oldScale * offsetY) / area.height],
    zoomFactor
  );
  const newCenterX = zoomed.left + (zoomed.right - zoomed.left) / 2;
  const newCenterY = zoomed.top + (zoomed.bottom - zoomed.top) / 2;
  const newScale = zoomed.right - zoomed.left;
  if (newScale < SCALE_MINIMUM) {
    return { windowScale, windowCenter };
  }
  return {
    windowScale: newScale,
    windowCenter: [newCenterX, newCenterY],
  };
};

export const pan = (
  area: NumericRange,
  [centerX, centerY]: Point,
  scale: number,
  [panningStartX, panningStartY]: Point,
  [panningEndX, panningEndY]: Point
): Point => {
  const centerMax = 1 - scale / 2;
  const centerMin = scale / 2;
  const panDistanceX = panningEndX - panningStartX;
  const panDistanceY = panningEndY - panningStartY;

  const distanceX = (panDistanceX / area[0]) * scale;
  const distanceY = (panDistanceY / area[1]) * scale;
  const originalX = centerX;
  const originalY = centerY;
  const newCenterX = clamp(originalX - distanceX, centerMin, centerMax);
  const newCenterY = clamp(originalY - distanceY, centerMin, centerMax);
  return [newCenterX, newCenterY];
};
